import type { AdType, GetProductSetQuery } from '../../generated/graphql-types';
import type { AdBudgetInfo } from './context/reducer';
import { isAnz } from '../../utils/isAnz';

interface BaseDiscount {
  type: 'FixedAmount' | 'Percentage';
  adType: 'Classic' | 'Basic' | 'Branded' | 'StandOut' | 'Premium';
}

type FixedAmountDiscount = BaseDiscount & {
  type: 'FixedAmount';
  value: number;
};

type PercentageDiscount = BaseDiscount & {
  type: 'Percentage';
  value: number;
};

type ProductSet = Extract<
  GetProductSetQuery['getProductSet'],
  { __typename: 'ProductSet' }
>;

type AdBudgetItem = ProductSet['items'][number];

type AdBudgetProductItem = Extract<
  AdBudgetItem,
  { __typename: 'AdBudgetProduct' }
>;

type AdBudgetProductItemPrice = Extract<
  AdBudgetProductItem['price'],
  { __typename: 'Price' }
>;

export type AdBudgetProductItemType = Omit<AdBudgetProductItem, 'price'> & {
  price: AdBudgetProductItemPrice;
};

const getDisplayAdType = (
  adType: AdType,
  country: string,
): 'Classic' | 'Basic' | 'StandOut' | 'Branded' | 'Premium' => {
  if (adType === 'CLASSIC' && isAnz(country)) {
    return 'Classic';
  }
  if (adType === 'CLASSIC') {
    return 'Basic';
  }
  if (
    (adType === 'BRANDED_UPGRADE' || adType === 'BRANDED') &&
    isAnz(country)
  ) {
    return 'StandOut';
  }
  if (adType === 'BRANDED_UPGRADE' || adType === 'BRANDED') {
    return 'Branded';
  }
  if (adType === 'PREMIUM') {
    return 'Premium';
  }
  throw new Error('Invalid adType');
};

const mapAdBudgetInfo = (
  adBudgetProduct: AdBudgetProductItemType,
  country: string,
): AdBudgetInfo => {
  const durationFeature = adBudgetProduct.features.flatMap((feature) =>
    feature?.__typename === 'DurationFeature' ? [feature] : [],
  )[0];

  if (durationFeature === undefined) {
    throw new Error('no duration feature');
  }

  const discounts = adBudgetProduct.features
    .flatMap((feature) =>
      feature?.__typename === 'AdTypeDiscountsFeature'
        ? [
            feature.discounts.flatMap(
              (
                discountFeature,
              ): Array<FixedAmountDiscount | PercentageDiscount> => {
                if (
                  discountFeature.discount.discountPercentage > 0 &&
                  discountFeature.discount.discountAmount.value > 0
                ) {
                  throw new Error(
                    'fixed and percentage discounts specified in single feature',
                  );
                }
                if (discountFeature.discount.discountPercentage > 0) {
                  return [
                    {
                      type: 'Percentage',
                      adType: getDisplayAdType(discountFeature.adType, country),
                      value: discountFeature.discount.discountPercentage,
                    },
                  ];
                }
                if (discountFeature.discount.discountAmount.value > 0) {
                  return [
                    {
                      type: 'FixedAmount',
                      adType: getDisplayAdType(discountFeature.adType, country),
                      value: discountFeature.discount.discountAmount.value,
                    },
                  ];
                }
                return [];
              },
            ),
          ]
        : [],
    )
    .flat();

  const isBrandedFeaturesFeaturePresent = Boolean(
    adBudgetProduct.features.find(
      (feature) => feature?.__typename === 'BrandedFeaturesFeature',
    ),
  );

  const adBudgetProductName = adBudgetProduct.name;

  return {
    id: adBudgetProduct.id,
    price: adBudgetProduct.price.netPrice.value,
    currency: adBudgetProduct.price.netPrice.currency,
    name: adBudgetProductName,
    displayItemTitle: adBudgetProductName.substring(
      0,
      adBudgetProductName.indexOf('Budget') + 'Budget'.length,
    ),
    discountDurationMonths: durationFeature.value,
    percentageSavings:
      discounts.find((discount) => discount.type === 'Percentage')?.value ?? 0,
    ads: isBrandedFeaturesFeaturePresent
      ? 'BRANDED_ONWARDS'
      : 'CLASSIC_ONWARDS',
  };
};

export { mapAdBudgetInfo };
